import {
  EyeInvisibleOutlined,
  EyeOutlined,
  LeftOutlined,
  MinusOutlined,
  PlusOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Button, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../lib/auth';
import { wsUrl } from '../lib/config';
import { useWebSocket } from '../lib/ws';
import './SlideController.css';

const { Text } = Typography;

type IncomingMessage =
  | { type: 'notes'; text: string; slideNumber: number; totalSlides: number }
  | { type: 'app-status'; connected: boolean }
  | { type: 'state'; enabled: boolean };

const FONT_SIZE_KEY = 'slideNotes.fontSize';
const FONT_SIZE_MIN = 0.8;
const FONT_SIZE_MAX = 2.4;
const FONT_SIZE_STEP = 0.2;
const FONT_SIZE_DEFAULT = 1.2;

export function SlideController({ slideKey }: { slideKey: string }) {
  const { session } = useAuth();
  const [notes, setNotes] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(true);
  const [appConnected, setAppConnected] = useState(false);
  const [connecting, setConnecting] = useState(true);
  const [notesFontSize, setNotesFontSize] = useState<number>(() => {
    const saved = parseFloat(localStorage.getItem(FONT_SIZE_KEY) ?? '');
    return Number.isFinite(saved) && saved >= FONT_SIZE_MIN && saved <= FONT_SIZE_MAX
      ? saved
      : FONT_SIZE_DEFAULT;
  });

  useEffect(() => {
    localStorage.setItem(FONT_SIZE_KEY, notesFontSize.toFixed(2));
  }, [notesFontSize]);

  const bumpFontSize = (delta: number) => {
    setNotesFontSize((v) => {
      const next = Math.round((v + delta) * 100) / 100;
      return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, next));
    });
  };

  const url = useMemo(() => {
    if (!session) return null;
    return wsUrl(
      `/api/slides/${encodeURIComponent(slideKey)}?role=controller&token=${encodeURIComponent(session.token)}`,
    );
  }, [slideKey, session]);

  const { state, send } = useWebSocket<IncomingMessage>(url, (msg) => {
    if (msg.type === 'notes') {
      setNotes((msg.text ?? '').replace(/\r/g, '\n'));
    } else if (msg.type === 'app-status') {
      setAppConnected(msg.connected);
    }
  });

  useEffect(() => {
    if (state === 'open') setConnecting(false);
  }, [state]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') send({ type: 'prev' });
      if (e.key === 'ArrowRight') send({ type: 'next' });
    };
    document.body.addEventListener('keydown', onKey);
    return () => document.body.removeEventListener('keydown', onKey);
  }, [send]);

  const hasHtml = useMemo(() => /<\/?[a-z][\s\S]*>/i.test(notes ?? ''), [notes]);
  const notesEmpty = !notes || !notes.trim();
  const notesFontSizeEm = `${notesFontSize}em`;
  const canShrink = notesFontSize > FONT_SIZE_MIN + 1e-6;
  const canGrow = notesFontSize < FONT_SIZE_MAX - 1e-6;

  return (
    <>
      <div className={`mobileNotes notes${showNotes ? '' : ' hiddenNotes'}`}>
        {notesEmpty ? (
          <p style={{ color: '#fff', fontStyle: 'italic', margin: 0 }}>
            {appConnected ? 'No notes' : 'Slides not connected'}
          </p>
        ) : (
          <div
            style={{
              color: '#fff',
              fontSize: notesFontSizeEm,
              margin: 0,
              whiteSpace: 'pre-wrap',
            }}
            dangerouslySetInnerHTML={{ __html: notes ?? '' }}
          />
        )}
      </div>
      <div className="desktopBarHolder">
        {connecting ? (
          <Text type="secondary" style={{ padding: '1em' }}>
            Connecting to presentation controls…
          </Text>
        ) : (
          <div
            style={{
              width: '100%',
              maxWidth: 1200,
              pointerEvents: 'auto',
              paddingLeft: '0.7em',
              paddingRight: '0.7em',
              marginTop: '0.55em',
              marginBottom: '0.55em',
              display: 'flex',
              alignItems: 'flex-start',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ flex: 1, textAlign: 'right', minWidth: 0 }}>
              <Button
                className="slideButtons previousMobile"
                size="large"
                shape="round"
                danger
                type="primary"
                style={{ background: '#d81b60', borderColor: '#d81b60', color: '#fff' }}
                onClick={() => send({ type: 'prev' })}
                disabled={!appConnected}
              >
                <LeftOutlined />
                Prev Slide
              </Button>
              <Button
                shape="round"
                size="large"
                icon={showNotes ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                onClick={() => setShowNotes((v) => !v)}
                style={{ marginLeft: 8 }}
              >
                {showNotes ? 'Hide Notes' : 'Show Notes'}
              </Button>
              {showNotes && (
                <>
                  <Button
                    shape="circle"
                    size="large"
                    icon={<MinusOutlined />}
                    onClick={() => bumpFontSize(-FONT_SIZE_STEP)}
                    disabled={!canShrink}
                    style={{ marginLeft: 8 }}
                    aria-label="Smaller notes"
                  />
                  <Button
                    shape="circle"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={() => bumpFontSize(FONT_SIZE_STEP)}
                    disabled={!canGrow}
                    style={{ marginLeft: 8 }}
                    aria-label="Larger notes"
                  />
                </>
              )}
            </div>
            {showNotes && (
              <div style={{ flex: 6 }}>
                <div
                  className={`desktopNotes notes${hasHtml ? ' includesHTML' : ''}`}
                >
                  {notesEmpty ? (
                    <p
                      style={{
                        color: '#fff',
                        fontStyle: 'italic',
                        margin: 0,
                      }}
                    >
                      {appConnected ? 'No notes' : 'Slides not connected'}
                    </p>
                  ) : (
                    <div
                      style={{
                        color: '#fff',
                        fontSize: notesFontSizeEm,
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                      }}
                      dangerouslySetInnerHTML={{ __html: notes ?? '' }}
                    />
                  )}
                </div>
              </div>
            )}
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: showNotes ? 'column' : 'row-reverse',
                justifyContent: showNotes ? 'flex-start' : 'flex-end',
                alignItems: 'flex-start',
              }}
            >
              <Button
                className="slideButtons nextDesktop"
                size="large"
                shape="round"
                type="primary"
                style={{ background: '#009e73', borderColor: '#009e73', color: '#fff' }}
                onClick={() => send({ type: 'next' })}
                disabled={!appConnected}
              >
                Next Slide
                <RightOutlined />
              </Button>
              <Button
                className="slideButtons previousDesktop"
                size="large"
                shape="round"
                danger
                type="primary"
                style={{
                  background: '#d81b60',
                  borderColor: '#d81b60',
                  color: '#fff',
                  marginTop: showNotes ? '0.5em' : 0,
                  marginRight: !showNotes ? '0.4em' : 0,
                }}
                onClick={() => send({ type: 'prev' })}
                disabled={!appConnected}
              >
                <LeftOutlined />
                Prev Slide
              </Button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
